/**
 * Sister Diamonds Platform
 * 
 * Copyright (c) 2024-2026 Sister Diamonds, LLC. All rights reserved.
 * A Volition Enterprises Company.
 * 
 * This software is proprietary and confidential. Unauthorized copying,
 * modification, distribution, or use of this software, via any medium,
 * is strictly prohibited without express written permission.
 * 
 * Contact: info@sisterdiamonds.com
 */

import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CelebrationProvider } from "@/context/CelebrationContext";
import axios from "axios";

// Hooks
import { usePageTracking } from "@/hooks/usePageTracking";

// Components
import SHEChatWidget from "@/components/SHEChatWidget";
import SHEQuickGuide from "@/components/SHEQuickGuide";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import INeedSupportButton from "@/components/INeedSupportButton";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";

// Intentionally throwing component used to validate the global ErrorBoundary.
// Mounted on /__boundary_smoke_test — safe to leave in; no public link.
const BoundarySmokeTest = () => {
  throw new Error('BOUNDARY_SMOKE_TEST_ERROR');
};

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PathwaysPage from "@/pages/PathwaysPage";
import InnerCirclePage from "@/pages/InnerCirclePage";
import CoachesPage from "@/pages/CoachesPage";
import CoachProfilePage from "@/pages/CoachProfilePage";
import CommunitiesPage from "@/pages/CommunitiesPage";
import CommunityDetailPage from "@/pages/CommunityDetailPage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import VideoPlayerPage from "@/pages/VideoPlayerPage";
import MessagesPage from "@/pages/MessagesPage";
import CalendarPage from "@/pages/CalendarPage";
import SessionsPage from "@/pages/SessionsPage";
import LegacyCircleHubPage from "@/pages/LegacyCircleHubPage";
import SeasonLandingPage from "@/pages/SeasonLandingPage";
import BelovedAgreementsPage from "@/pages/BelovedAgreementsPage";
import DiagnosticPage from "@/pages/DiagnosticPage";
import InnerEdgeHubPage from "@/pages/InnerEdgeHubPage";
import InnerEdgePaymentPage from "@/pages/InnerEdgePaymentPage";
import RetreatHubPage from "@/pages/RetreatHubPage";
import RetreatLandingPage from "@/pages/RetreatLandingPage";
import RetreatsHubPage from "@/pages/RetreatsHubPage";
import FAQPage from "@/pages/FAQPage";
import LegacyCirclePage from "@/pages/LegacyCirclePage";
import JustBeFoundersPage from "@/pages/JustBeFoundersPage";
import RetreatBookingPage from "@/pages/RetreatBookingPage";
import RetreatCheckoutPage from "@/pages/RetreatCheckoutPage";
import RetreatWelcomePage from "@/pages/RetreatWelcomePage";
import RetreatParticipantFormPage from "@/pages/RetreatParticipantFormPage";
import SetupAccountPage from "@/pages/SetupAccountPage";
import SDCLoungePage from "@/pages/SDCLoungePage";
import SDCHubPage from "@/pages/SDCHubPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import AdminManagementPage from "@/pages/AdminManagementPage";
import WonderTwinDashboardPage from "@/pages/WonderTwinDashboardPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CoachDashboardPage from "@/pages/CoachDashboardPage";
import MemberDirectoryPage from "@/pages/MemberDirectoryPage";
import JournalPage from "@/pages/JournalPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import AdminGiftDashboardPage from "@/pages/AdminGiftDashboardPage";
import SavedPostsPage from "@/pages/SavedPostsPage";
import CirclesPage from "@/pages/CirclesPage";
import CircleDetailPage from "@/pages/CircleDetailPage";
import CommunityGuidelinesPage from "@/pages/CommunityGuidelinesPage";
import BooksPage from "@/pages/BooksPage";
import FounderProfilePage from "@/pages/FounderProfilePage";
import GiftMembershipPage from "@/pages/GiftMembershipPage";
import AcceptGiftPage from "@/pages/AcceptGiftPage";
import ActivationPathwayPage from "@/pages/ActivationPathwayPage";
import YummyDeclarationPage from "@/pages/YummyDeclarationPage";
import CircleChatPage from "@/pages/CircleChatPage";
import MyJourneyPage from "@/pages/MyJourneyPage";
import FavoritesSoulFoodPage from "@/pages/FavoritesSoulFoodPage";
import MilestoneNotification from "@/components/MilestoneNotification";
// Growth Tools Pages
import TruthResetPage from "@/pages/TruthResetPage";
import SisterSpotlightsPage from "@/pages/SisterSpotlightsPage";
import LegacyVisionPage from "@/pages/LegacyVisionPage";
import ChallengesPage from "@/pages/ChallengesPage";
import LibraryPage from "@/pages/LibraryPage";
import TermsPage from "@/pages/TermsPage";
import SDCPaymentPage from "@/pages/SDCPaymentPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CollectiveApplicationPage from "@/pages/CollectiveApplicationPage";
import AdminCoachProfilePage from "@/pages/AdminCoachProfilePage";
import CoachPortalPage from "@/pages/CoachPortalPage";
import SoulFoodSharePage from "@/pages/SoulFoodSharePage";
import DiamondResetPage from "@/pages/DiamondResetPage";
import ResetRoomPage from "@/pages/ResetRoomPage";
import GoDeeper from "@/pages/GoDeeper";
import ResetGiftPage from "@/pages/ResetGiftPage";
import GiftApplicationPage from "@/pages/GiftApplicationPage";
import AffiliateRegisterPage from "@/pages/AffiliateRegisterPage";
import AffiliateLoginPage from "@/pages/AffiliateLoginPage";
import AffiliateDashboardPage from "@/pages/AffiliateDashboardPage";

// Protected Route Component (requires login)
const ProtectedRoute = ({ children, skipOnboarding = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to onboarding if not completed (unless we're already on onboarding page or skipOnboarding is true)
  if (!skipOnboarding && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Tier-gated Route Component
const TierRoute = ({ children, requiredTiers, redirectTo = "/pathways" }) => {
  const { user, loading, hasAnyTier } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Allow admin and super_admin users to bypass tier restrictions
  if (user.role === 'admin' || user.role === 'super_admin') {
    return children;
  }

  if (!hasAnyTier(requiredTiers)) {
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  // Allow both admin and super_admin roles
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Coach Route Component (for coaches and admins)
const CoachRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  // Allow coaches, admins, and super_admins
  if (!user || (!user.is_coach && user.role !== 'admin' && user.role !== 'super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const RetreatsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/retreats${location.search}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CelebrationProvider>
          <div className="App min-h-screen bg-cream">
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/__boundary_smoke_test" element={<BoundarySmokeTest />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/setup-account" element={<SetupAccountPage />} />
            <Route path="/pathways" element={<PathwaysPage />} />
            <Route path="/inner-circle" element={<InnerCirclePage />} />
            <Route path="/the-inner-edge" element={<InnerEdgePaymentPage />} />
            <Route path="/coaches" element={<CoachesPage />} />
            <Route path="/coaches/founder-crystal-nicole" element={<FounderProfilePage />} />
            <Route path="/coaches/:coachId" element={<CoachProfilePage />} />
            
            {/* Public Retreats Hub + archived Costa Rica retrospective (accessible without login) */}
            <Route path="/retreats" element={<RetreatsHubPage />} />
            <Route path="/retreats/costa-rica-2026" element={<RetreatLandingPage />} />
            <Route path="/costa-rica-retreat" element={<RetreatsRedirect />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/just-be-retreat" element={<RetreatsRedirect />} />
            <Route path="/legacy-circle" element={<LegacyCirclePage />} />
            <Route path="/the-legacy-circle" element={<LegacyCirclePage />} />
            <Route path="/just-be-founders" element={<JustBeFoundersPage />} />
            <Route path="/beloved-agreements" element={<BelovedAgreementsPage />} />
            <Route path="/costa-rica-founders" element={<JustBeFoundersPage />} />
            <Route path="/retreat-booking" element={<RetreatBookingPage />} />
            <Route path="/retreat-checkout" element={<RetreatCheckoutPage />} />
            <Route path="/retreat-welcome" element={<RetreatWelcomePage />} />
            <Route path="/retreat-participant-form" element={<RetreatParticipantFormPage />} />
            
            {/* Legal Pages (public) */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sdc-payment/:token" element={<SDCPaymentPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            
            {/* Gift Membership (public page to accept gifts) */}
            <Route path="/accept-gift/:giftCode" element={<AcceptGiftPage />} />
            
            {/* Collective Application (hidden - public page for vetting) */}
            <Route path="/collective-application" element={<CollectiveApplicationPage />} />
            <Route path="/diamond-reset" element={<DiamondResetPage />} />
            <Route path="/reset-room" element={<ResetRoomPage />} />
            <Route path="/go-deeper" element={<GoDeeper />} />
            
            {/* Gift Retreat Application (public) */}
            <Route path="/reset-gift" element={<ResetGiftPage />} />
            <Route path="/reset-gift/apply" element={<GiftApplicationPage />} />
            
            {/* Affiliate Portal (public) */}
            <Route path="/affiliate/register" element={<AffiliateRegisterPage />} />
            <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
            
            {/* Coach Portal (public landing page for coaches) */}
            <Route path="/coach-portal" element={<CoachPortalPage />} />
            
            {/* Public Soul Food Share Page */}
            <Route path="/share/soul-food/:soulFoodId" element={<SoulFoodSharePage />} />
            
            {/* Protected Routes (login required) */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute skipOnboarding={true}><OnboardingPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/checkout/success" element={<ProtectedRoute skipOnboarding={true}><CheckoutSuccessPage /></ProtectedRoute>} />
            <Route path="/gift-membership" element={<ProtectedRoute><GiftMembershipPage /></ProtectedRoute>} />
            <Route path="/activation-pathway" element={<ProtectedRoute><ActivationPathwayPage /></ProtectedRoute>} />
            <Route path="/yummy-declaration" element={<ProtectedRoute><YummyDeclarationPage /></ProtectedRoute>} />
            
            {/* Inner Circle+ Routes */}
            <Route path="/communities" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CommunitiesPage />
              </TierRoute>
            } />
            <Route path="/communities/:communityId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CommunityDetailPage />
              </TierRoute>
            } />
            <Route path="/videos" element={
              <ProtectedRoute>
                <VideoLibraryPage />
              </ProtectedRoute>
            } />
            <Route path="/videos/:videoId" element={
              <ProtectedRoute>
                <VideoPlayerPage />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MessagesPage />
              </TierRoute>
            } />
            <Route path="/messages/:partnerId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MessagesPage />
              </TierRoute>
            } />
            
            {/* Inner Edge+ Routes */}
            <Route path="/diagnostic" element={
              <TierRoute requiredTiers={['inner_edge']}>
                <DiagnosticPage />
              </TierRoute>
            } />
            <Route path="/inner-edge" element={
              <TierRoute requiredTiers={['inner_edge']}>
                <InnerEdgeHubPage />
              </TierRoute>
            } />
            
            {/* Coaching Routes */}
            <Route path="/sessions" element={
              <TierRoute requiredTiers={['inner_edge', 'coaching_activation', 'sdc']}>
                <SessionsPage />
              </TierRoute>
            } />
            <Route path="/calendar" element={
              <TierRoute requiredTiers={['inner_edge', 'coaching_activation', 'sdc']}>
                <CalendarPage />
              </TierRoute>
            } />
            
            {/* Costa Rica Routes */}
            <Route path="/retreat" element={
              <TierRoute requiredTiers={['costa_rica']}>
                <RetreatHubPage />
              </TierRoute>
            } />
            
            {/* SDC Routes */}
            <Route path="/sdc" element={
              <TierRoute requiredTiers={['sdc']}>
                <SDCHubPage />
              </TierRoute>
            } />
            <Route path="/sdc-lounge" element={
              <TierRoute requiredTiers={['sdc']}>
                <SDCLoungePage />
              </TierRoute>
            } />
            
            {/* Member Directory & Journal Routes */}
            <Route path="/directory" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MemberDirectoryPage />
              </TierRoute>
            } />
            <Route path="/journal" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <JournalPage />
              </TierRoute>
            } />
            <Route path="/saved" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <SavedPostsPage />
              </TierRoute>
            } />
            <Route path="/favorites/soul-food" element={
              <TierRoute requiredTiers={['inner_circle']}>
                <FavoritesSoulFoodPage />
              </TierRoute>
            } />
            <Route path="/books" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <BooksPage />
              </TierRoute>
            } />
            <Route path="/my-journey" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MyJourneyPage />
              </TierRoute>
            } />
            
            {/* Community Circles Routes */}
            <Route path="/circles" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CirclesPage />
              </TierRoute>
            } />
            <Route path="/legacy" element={
              <TierRoute requiredTiers={['legacy_circle']} redirectTo="/dashboard">
                <LegacyCircleHubPage />
              </TierRoute>
            } />
            <Route path="/legacy/seasons" element={
              <TierRoute requiredTiers={['legacy_circle']} redirectTo="/dashboard">
                <SeasonLandingPage />
              </TierRoute>
            } />
            <Route path="/circles/:circleId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc', 'costa_rica']}>
                <CircleDetailPage />
              </TierRoute>
            } />
            
            {/* Accountability Circle Chat */}
            <Route path="/accountability-circle" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc', 'costa_rica']}>
                <CircleChatPage />
              </TierRoute>
            } />
            <Route path="/accountability-circle/:circleId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc', 'costa_rica']}>
                <CircleChatPage />
              </TierRoute>
            } />
            
            {/* Community Guidelines */}
            <Route path="/community-guidelines" element={
              <ProtectedRoute>
                <CommunityGuidelinesPage />
              </ProtectedRoute>
            } />
            
            {/* Growth Tools Routes */}
            <Route path="/truth-reset" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <TruthResetPage />
              </TierRoute>
            } />
            <Route path="/sister-spotlights" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <SisterSpotlightsPage />
              </TierRoute>
            } />
            <Route path="/legacy-vision" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <LegacyVisionPage />
              </TierRoute>
            } />
            <Route path="/challenges" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <ChallengesPage />
              </TierRoute>
            } />
            <Route path="/library" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <LibraryPage />
              </TierRoute>
            } />
            
            {/* Coach Dashboard Routes */}
            <Route path="/coach" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/profile" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/sessions" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/settings" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/clients" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/clients/:userId" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/notes" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/invoices" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/agreements" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/notifications" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/messages" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/messages/:partnerId" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/legacy" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/leads" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/coaches" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/group-sessions" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/requests" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/coach-notes" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/invoices" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/billing" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/messages" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/support" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/safety" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/esign" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/coaches/:coachId" element={<AdminRoute><AdminCoachProfilePage /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
            <Route path="/admin/gifts" element={<AdminRoute><AdminGiftDashboardPage /></AdminRoute>} />
            <Route path="/admin/gift-applications" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/affiliates" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/manage-admins" element={<AdminRoute><AdminManagementPage /></AdminRoute>} />
            <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />
            
            {/* Wonder Twin Dashboard */}
            <Route path="/wonder-twin" element={<AdminRoute><WonderTwinDashboardPage /></AdminRoute>} />
          </Routes>
          <PageTrackingWrapper />
          <SHEChatWrapper />
          <SHEQuickGuide />
          <MilestoneNotificationWrapper />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </div>
      </CelebrationProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

// Wrapper for Milestone Notification - only shows on dashboard for logged-in users
const MilestoneNotificationWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Only show on dashboard
  if (!user || location.pathname !== '/dashboard') {
    return null;
  }
  
  return <MilestoneNotification />;
};

// Page tracking wrapper - tracks all page views for engagement analytics
const PageTrackingWrapper = () => {
  // Track page views for engagement analytics
  usePageTracking();
  return null;
};

// Wrapper component to provide page context to SHE chat
const SHEChatWrapper = () => {
  const location = useLocation();
  
  const getPageContext = () => {
    const path = location.pathname;
    
    const pageContextMap = {
      '/': `
        Page: Sister Diamonds Landing Page
        This is the main landing page for Sister Diamonds Collective.
        Key offerings:
        - Inner Circle membership ($97/month): Access to community, video library, weekly transmissions
        - Just Be: Soft Power Retreat in Costa Rica (June 26 - July 1, 2026): A luxury retreat for high-capacity women
        - Coaching programs including Inner Edge ($5,000) and SDC ($25,000)
        The mission is to help high-achieving women leaders find balance, restore their energy, and lead with both strength and softness.
      `,
      '/costa-rica-retreat': `
        Page: Just Be: Soft Power™ Retreat - Costa Rica
        Location: Imiloa Institute, Costa Rica
        Dates: June 26 – July 1, 2026
        This is a soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise in their softness, not their striving.
        Hosts: Crystal and Dr. Nicole (the "Wonder Twins")
        Features: Luxury accommodations, transformational experiences, sisterhood, rest and restoration
        Pricing: Various accommodation options available
        To learn more or express interest, visitors can book a connection call.
      `,
      '/just-be-retreat': `
        Page: Just Be: Soft Power™ Retreat - Costa Rica
        Location: Imiloa Institute, Costa Rica
        Dates: June 26 – July 1, 2026
        This is a soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise in their softness, not their striving.
      `,
      '/login': `
        Page: Login
        Users can log in to access their Sister Diamonds membership, coaching sessions, and community features.
      `,
      '/register': `
        Page: Register / Join Inner Circle
        New members can sign up for the Inner Circle membership ($97/month).
        Benefits include: Community access, video library, weekly transmissions, and connection with like-minded women leaders.
      `,
      '/dashboard': `
        Page: Member Dashboard
        This is the main dashboard for logged-in members showing their membership status, upcoming sessions, and quick access to features.
      `,
      '/coaches': `
        Page: Coaches Directory
        Browse the full directory of certified Sister Diamonds coaches.
        Each coach has specialties and can help with different aspects of leadership, personal development, and transformation.
      `,
      '/communities': `
        Page: Communities
        Access to various community spaces for discussion, support, and connection with other members.
      `,
      '/videos': `
        Page: Video Library
        Access to training videos, workshops, and educational content for members.
      `,
      '/diagnostic': `
        Page: Diagnostic Assessment
        A comprehensive LLM-powered diagnostic to assess your current state across multiple life dimensions.
        Results help match you with the right coach and provide insights for your growth journey.
      `,
      '/inner-edge': `
        Page: Inner Edge Hub
        The Inner Edge program ($5,000) includes:
        - Comprehensive diagnostic assessment
        - Coach matching based on your results
        - 6 coaching sessions with your matched coach
        - 2 founder sessions with Crystal and Dr. Nicole
        - Full community and app access
      `,
      '/sdc': `
        Page: SDC (Sister Diamond Collective) Hub
        The SDC program ($25,000 VIP tier) includes:
        - 3 chemistry sessions with different coaches (15 minutes each) to find your perfect match
        - Selection of your dedicated coach
        - 12 one-hour coaching sessions with your selected coach
        - 3 Wonder Twin sessions with Crystal and Dr. Nicole
        - 10% discount on all retreats and masterminds
        - Full app and community access
        - SDC Lounge access with Hot Seat and Concierge services
      `,
      '/sdc-lounge': `
        Page: SDC Lounge
        VIP-only space for SDC members featuring:
        - Hot Seat sessions: Submit topics for live coaching
        - Concierge service: Personal assistance and introductions
        - Exclusive VIP events and content
      `,
      '/pathways': `
        Page: Pathways
        Explore different membership tiers and coaching programs:
        - Inner Circle ($97/month): Community and content access
        - Inner Edge ($5,000): Comprehensive coaching program
        - SDC ($25,000): VIP transformation experience
      `,
      '/sessions': `
        Page: Sessions
        View and manage your upcoming and past coaching sessions.
        Book new sessions with your coach or the founders.
      `,
      '/settings': `
        Page: Settings
        Manage your account settings, profile information, and preferences.
      `
    };
    
    return pageContextMap[path] || `Page: ${path}\nThis is a page within the Sister Diamonds platform.`;
  };

  return <SHEChatWidget pageContext={getPageContext()} />;
}

export default App;
